import { X } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';

interface BenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const benefitsTranslations = {
  ko: {
    title: '로그인하면 받을 수 있는 혜택',
    close: '닫기',
    benefits: [
      {
        icon: '⭐',
        title: '포인트 적립',
        description: '주문 금액의 일부를 포인트로 적립해드립니다'
      },
      {
        icon: '🎂',
        title: '생일 쿠폰',
        description: '생일 인증 시 특별 할인 쿠폰을 받으세요'
      },
      {
        icon: '🎫',
        title: '브랜드 쿠폰',
        description: '다양한 브랜드의 독점 쿠폰을 받으세요'
      },
      {
        icon: '🚀',
        title: '빠른 주문',
        description: '저장된 정보로 빠르고 편리한 주문'
      },
      {
        icon: '📱',
        title: '주문 내역 관리',
        description: '모든 주문 내역을 한눈에 확인하세요'
      },
      {
        icon: '🔔',
        title: '특별 알림',
        description: '맞춤형 프로모션 및 이벤트 알림'
      }
    ]
  },
  es: {
    title: 'Beneficios al iniciar sesión',
    close: 'Cerrar',
    benefits: [
      {
        icon: '⭐',
        title: 'Acumulación de puntos',
        description: 'Acumula una parte de tu compra en puntos'
      },
      {
        icon: '🎂',
        title: 'Cupón de cumpleaños',
        description: 'Cupón especial al verificar tu cumpleaños'
      },
      {
        icon: '🎫',
        title: 'Cupones exclusivos',
        description: 'Accede a cupones de tus marcas favoritas'
      },
      {
        icon: '🚀',
        title: 'Pedidos rápidos',
        description: 'Ordena más rápido con información guardada'
      },
      {
        icon: '📱',
        title: 'Historial de pedidos',
        description: 'Revisa todos tus pedidos anteriores'
      },
      {
        icon: '🔔',
        title: 'Notificaciones especiales',
        description: 'Recibe promociones y eventos personalizados'
      }
    ]
  },
  en: {
    title: 'Benefits of logging in',
    close: 'Close',
    benefits: [
      {
        icon: '⭐',
        title: 'Points accumulation',
        description: 'Earn a portion of your purchase back in points'
      },
      {
        icon: '🎂',
        title: 'Birthday coupon',
        description: 'Special discount when you verify your birthday'
      },
      {
        icon: '🎫',
        title: 'Exclusive coupons',
        description: 'Get exclusive coupons from brands'
      },
      {
        icon: '🚀',
        title: 'Fast ordering',
        description: 'Order faster with saved information'
      },
      {
        icon: '📱',
        title: 'Order history',
        description: 'View all your past orders at a glance'
      },
      {
        icon: '🔔',
        title: 'Special notifications',
        description: 'Receive personalized promotions and events'
      }
    ]
  }
};

export function BenefitsModal({ isOpen, onClose }: BenefitsModalProps) {
  const { language } = useLanguage();
  const t = benefitsTranslations[language];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[100] animate-fadeIn"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div 
          className="bg-background rounded-[10px] w-full max-w-lg max-h-[85vh] overflow-hidden shadow-xl animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-primary px-5 py-4 flex items-center justify-between">
            <h2 className="text-white font-bold text-lg text-[14px]">
              {t.title}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-80px)] px-5 py-4 space-y-3 pt-[16px] pr-[20px] pb-[80px] pl-[20px]">
            {t.benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex gap-3 p-4 rounded-[10px] bg-muted hover:bg-muted/80 transition-colors"
                style={{
                  animation: `fadeInUp 0.4s ease-out ${index * 0.05}s backwards`
                }}
              >
                <div className="text-3xl shrink-0">
                  {benefit.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-foreground mb-1 text-[14px]">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-muted-foreground text-[12px]">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4">
            <button
              onClick={onClose}
              className="w-full bg-primary hover:bg-primary-hover text-white font-bold py-3 rounded-[5px] transition-colors active:scale-95 text-center"
            >
              {t.close}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}